import {Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  ClrConditionalModule,
  ClrDatepickerModule,
  ClrInputModule, ClrRadioModule,
  ClrSelectModule,
  ClrStepperModule
} from "@clr/angular";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { RequestCompanySearchComponent } from "../../requests/request-company-search/request-company-search.component";
import { Companies, Delivery, Personen, Staff, WebserviceResponse } from "../../../_shared/interfaces";
import { Subject } from "rxjs";
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import {
  AlertService, DeliveryStoreService,
  IAlertType, PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import {ClarityIcons, twoWayArrowsIcon} from "@cds/core/icon";
import {environment as env} from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-delivery-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrConditionalModule,
    RequestCompanySearchComponent,
    ClrInputModule,
    ClrSelectModule,
    NgIf,
    ClrDatepickerModule,
    DatePipe,
    RouterLink,
    ClrRadioModule,
    NgForOf,
    JsonPipe,
    TranslocoDirective
  ],
  templateUrl: './delivery-form.component.html',
  styleUrl: './delivery-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class DeliveryFormComponent implements OnInit, OnChanges {
  deliveryForm!: FormGroup;
  private defaultDate: Date = new Date();
  companyId: number = 0;
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.deliveryForm);
  }

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() deliveryId: number = 0;
  @Input() isUpdateMode = false;
  @Input() deliveryData?: Delivery;
  @Output() formSubmit = new EventEmitter<Delivery>();

  devMode: boolean = false;
  crmMode: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private delss: DeliveryStoreService,
    private staffService: StaffStoreService,
    private personsService: PersonsStoreService,
    private fb: FormBuilder,
    private router: Router,
    private alertDispatcher: AlertService,
    private datePipe: DatePipe
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
    this.initForm();
  }
  ngOnInit() {
    this.loadStaff();
    this.companyId = parseInt(this.splitProjectNumber(this.projectId, 0));
    this.loadPersons(this.companyId);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['deliveryData'] && this.deliveryData) {
      this.patchFormValues(this.deliveryData);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.deliveryForm = this.fb.group({
      stepI: this.fb.group({
        project_id: this.projectId,
        delivery_id: this.deliveryId,
        company_name: [''],
        department: [''],
        street: [''],
        postcode: [''],
        city: [''],
        country: [''],
        status: [''],
        name_project: new FormControl('', [Validators.required, Validators.maxLength(255)]),
        description: [''],
        request_id: [''],
        order_id:  [''],
        delivery_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        budget: 0,
      }),
      stepII: this.fb.group({
        employee_phm: 0,
      }),
      stepIII: this.fb.group({
        employee_leader: 0,
      })
    });
  }

  preFillRow(supplier: Companies) {
    this.deliveryForm = this.fb.group({
      stepI: this.fb.group({
        project_id: this.deliveryData?.project_id,
        delivery_id: this.deliveryData?.delivery_id,
        company_name: supplier['company'],
        department: supplier['department'],
        street: supplier['street'],
        postcode: supplier['postcode'],
        city: supplier['city'],
        country: this.mapCountryForForm(supplier['country']),
        status: 'open',
        name_project: this.deliveryData?.name_project,
        description: this.deliveryData?.description,
        request_id: this.deliveryData?.request_id.toString(),
        order_id: this.deliveryData?.order_id.toString(),
        delivery_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        budget: this.deliveryData?.budget
      }),
      stepII: this.fb.group({
        employee_phm: 0,
      }),
      stepIII: this.fb.group({
        employee_leader: 0,
      })
    });

    this.loadPersons(this.companyId)
  }

  private patchFormValues(data: Delivery){
    if (!data) return;

    const formValue = {
      stepI: {
        project_id: data.project_id || '',
        delivery_id: data.delivery_id || 0,
        company_name: data.company_name || '',
        department: data.department || '',
        street: data.street || '',
        postcode: data.postcode || '',
        city: data.city || '',
        country: this.mapCountryForForm(data.country || ''),
        status: data.status || '',
        name_project: data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || '',
        order_id: data.order_id || 0,
        delivery_date: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        budget: data.budget || 0,
      },
      stepII: {
        employee_phm: data.employee_phm || 0,
      },
      stepIII: {
        employee_leader: data.employee_leader || 0,
      }
    };
    this.deliveryForm.patchValue(formValue);
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  private normalizeCountryForBackend(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  projectLeader(): number {
    return this.deliveryForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number) {
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  updateDelivery() {
    if (this.deliveryForm.valid) {
      const formValue = this.deliveryForm.value;
      const payload = {
        ...formValue,
        stepI: {
          ...formValue.stepI,
          country: this.normalizeCountryForBackend(formValue?.stepI?.country),
        }
      };
      this.delss.updateDelivery(payload, this.jobId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Lieferschein erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(payload);
          this.router.navigate(['/delivery/details', this.projectId, this.jobId, this.deliveryId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren des Lieferscheins: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }
}
